/**
 * Builds a .docx from research project config with styling aligned to the web/PDF (cover band, section kickers, tables).
 */
import {
  BorderStyle,
  convertInchesToTwip,
  Document,
  LineRuleType,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  WidthType,
} from 'docx';

/** Hex RGB without # — matches ResearchProjectEditorPage / PDF palette */
const DOCX_THEME = {
  coverBg: '1e293b',
  coverTagline: '7dd3fc',
  coverTitle: 'f8fafc',
  coverMuted: 'cbd5e1',
  sectionText: '0f172a',
  body: '475569',
  cardLabel: '0284c7',
  borderSlate: 'e2e8f0',
  tableHeaderBg: 'f8fafc',
  sectionShade: 'f1f5f9',
  sectionBorderBlue: '2563eb',
} as const;

const TABLE_BORDER = {
  style: BorderStyle.SINGLE,
  size: 1,
  color: DOCX_THEME.borderSlate,
} as const;

type Config = Record<string, unknown>;

function asStr(v: unknown): string {
  return v == null ? '' : String(v);
}

function asStrArr(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.map((x) => String(x ?? ''));
}

function numField(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = parseFloat(v.replace(/,/g, ''));
    if (Number.isFinite(n)) return n;
  }
  return null;
}

type ParsedBibRef = {
  title?: string;
  author?: string;
  journal?: string;
  year?: string;
  raw: string;
};

function bibBraceField(block: string, name: string): string | undefined {
  const re = new RegExp(`${name}\\s*=\\s*\\{([^}]*)\\}`, 'i');
  const m = block.match(re);
  if (!m) return undefined;
  return m[1].replace(/\s+/g, ' ').trim() || undefined;
}

function splitBibtexBlob(text: string): string[] {
  const t = text.trim();
  if (!t) return [];
  return t
    .split(/(?=@\w+\s*\{)/i)
    .map((s) => s.trim())
    .filter(Boolean);
}

function parseBibtexEntryBlock(block: string): ParsedBibRef | null {
  const t = block.trim();
  if (!t) return null;
  const head = t.match(/^@(\w+)\s*\{\s*([^,\s]+)\s*,/i);
  if (!head) return null;
  return {
    title: bibBraceField(t, 'title'),
    author: bibBraceField(t, 'author'),
    journal: bibBraceField(t, 'journal') || bibBraceField(t, 'booktitle'),
    year: bibBraceField(t, 'year'),
    raw: t,
  };
}

function parseScientificReferences(v: unknown): ParsedBibRef[] {
  if (v == null) return [];
  if (typeof v === 'string') {
    return splitBibtexBlob(v).map(parseBibtexEntryBlock).filter((x): x is ParsedBibRef => x != null);
  }
  if (Array.isArray(v)) {
    const out: ParsedBibRef[] = [];
    v.forEach((item, i) => {
      if (typeof item === 'string') {
        const p = parseBibtexEntryBlock(item);
        if (p) out.push(p);
        else if (item.trim()) out.push({ raw: item.trim() });
      } else if (item && typeof item === 'object' && !Array.isArray(item)) {
        const o = item as Record<string, unknown>;
        out.push({
          title: asStr(o.title) || undefined,
          author: asStr(o.author) || undefined,
          journal: asStr(o.journal || o.booktitle) || undefined,
          year: asStr(o.year) || undefined,
          raw: JSON.stringify(item),
        });
      }
    });
    return out;
  }
  return [];
}

function formatReferenceLine(r: ParsedBibRef): string {
  const hasAny = r.author || r.year || r.title || r.journal;
  if (!hasAny) return r.raw;
  let s = '';
  if (r.author) s += r.author.replace(/\s+and\s+/gi, ', ');
  if (r.year) s += (s ? ' ' : '') + `(${r.year})`;
  if (r.title) s += (s ? '. ' : '') + r.title;
  if (r.journal) s += (s ? '. ' : '') + r.journal;
  return s.endsWith('.') ? s : `${s}.`;
}

function isAcuteMechanisticProfile(data: Config, studyType: string): boolean {
  const t = studyType.toLowerCase();
  if (t.includes('mechanistic')) return true;
  if (t.includes('acute') && (t.includes('pilot') || t.includes('session') || t.includes('stimulation'))) return true;
  if (data.study_profile === 'acute_mechanistic') return true;
  if (data.acute_mechanistic_pilot === true) return true;
  const co = data.cohort;
  if (co && typeof co === 'object' && !Array.isArray(co)) {
    const c = co as Record<string, unknown>;
    const d = asStr(c.design || c.study_design).toLowerCase();
    if (d.includes('acute') || d.includes('mechanistic') || d === 'single_arm_acute') return true;
  }
  return false;
}

function visitRecordLabel(v: Record<string, unknown>): string {
  return asStr(v.visit || v.name || v.title);
}

function shouldOmitRandomizationVisit(v: Record<string, unknown>, mechanistic: boolean, data: Config): boolean {
  if (!mechanistic) return false;
  if (data.include_randomization_visit === true) return false;
  const label = visitRecordLabel(v).toLowerCase();
  if (!/randomi[sz]ation|randomi[sz]ed/.test(label)) return false;
  const procs = Array.isArray(v.procedures) ? v.procedures : [];
  return procs.length === 0;
}

function armRecordIsSham(a: Record<string, unknown>): boolean {
  const role = asStr(a.role).toLowerCase();
  if (role === 'sham' || role === 'placebo') return true;
  return /\bsham\b|\bplacebo\b/i.test(asStr(a.name));
}

function cohortArmsForReadView(arms: unknown): unknown[] {
  if (!Array.isArray(arms)) return [];
  return arms.filter((arm) => {
    if (!arm || typeof arm !== 'object' || Array.isArray(arm)) return false;
    const a = arm as Record<string, unknown>;
    if (armRecordIsSham(a)) {
      const n = numField(a.size);
      if (n == null || n <= 0) return false;
    }
    return true;
  });
}

function outcomeEndpointList(
  outcomes: Record<string, unknown>,
  key: 'primary' | 'secondary' | 'exploratory'
): string[] {
  const plural =
    key === 'primary'
      ? 'primary_endpoints'
      : key === 'secondary'
        ? 'secondary_endpoints'
        : 'exploratory_endpoints';
  const arr = asStrArr(outcomes[plural]);
  if (arr.length > 0) return arr;
  const single = asStr(outcomes[key]).trim();
  return single ? [single] : [];
}

function humanizeKey(k: string): string {
  return k.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function activeProtocolLines(value: unknown, indent = ''): string[] {
  if (value == null) return [];
  if (typeof value === 'string') {
    const s = value.trim();
    return s ? [indent + s] : [];
  }
  if (Array.isArray(value)) {
    return value.flatMap((x) => activeProtocolLines(x, indent));
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    const o = value as Record<string, unknown>;
    const lines: string[] = [];
    for (const [k, v] of Object.entries(o)) {
      if (v == null || (typeof v === 'string' && !v.trim())) continue;
      lines.push(`${indent}${humanizeKey(k)}:`);
      lines.push(...activeProtocolLines(v, `${indent}  `));
    }
    return lines;
  }
  return [indent + String(value)];
}

function optionalBudgetList(budget: Record<string, unknown>): unknown[] {
  const o = budget.optional_costs ?? budget.optional_categories;
  return Array.isArray(o) ? o : [];
}

/** Dark “cover” block like the web hero (single full-width cell). */
function buildCoverTable(
  projectTitle: string,
  shortTitle: string,
  status: string,
  studySite: string,
  areas: string[],
  studyType: string
): Table {
  const cellChildren: Paragraph[] = [
    new Paragraph({
      children: [
        new TextRun({
          text: 'Research project',
          color: DOCX_THEME.coverTagline,
          bold: true,
          size: 22,
        }),
      ],
      spacing: { after: 140 },
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: projectTitle || 'Untitled research project',
          color: DOCX_THEME.coverTitle,
          bold: true,
          size: 56,
        }),
      ],
      spacing: { after: shortTitle ? 140 : 200 },
    }),
  ];
  if (shortTitle) {
    cellChildren.push(
      new Paragraph({
        children: [new TextRun({ text: shortTitle, color: DOCX_THEME.coverMuted, size: 28 })],
        spacing: { after: 200 },
      })
    );
  }
  const pills = [status, studySite, areas.filter(Boolean).join(', ')].filter(Boolean);
  if (pills.length) {
    cellChildren.push(
      new Paragraph({
        children: [new TextRun({ text: pills.join(' · '), color: DOCX_THEME.coverMuted, size: 22 })],
        spacing: { after: studyType ? 140 : 220 },
      })
    );
  }
  if (studyType) {
    cellChildren.push(
      new Paragraph({
        children: [new TextRun({ text: studyType, color: DOCX_THEME.coverMuted, size: 26 })],
        spacing: { after: 220 },
      })
    );
  }
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: {
      top: TABLE_BORDER,
      bottom: TABLE_BORDER,
      left: TABLE_BORDER,
      right: TABLE_BORDER,
    },
    rows: [
      new TableRow({
        children: [
          new TableCell({
            shading: { fill: DOCX_THEME.coverBg },
            margins: { top: 400, bottom: 400, left: 420, right: 420 },
            children: cellChildren,
          }),
        ],
      }),
    ],
  });
}

/** Section kicker style: blue left rule + light fill (matches SectionKicker on web). */
function sectionTitle(text: string): Paragraph {
  return new Paragraph({
    spacing: { before: 280, after: 200 },
    border: {
      left: {
        color: DOCX_THEME.sectionBorderBlue,
        style: BorderStyle.SINGLE,
        size: 28,
      },
    },
    shading: { fill: DOCX_THEME.sectionShade },
    indent: { left: 220 },
    children: [
      new TextRun({
        text,
        bold: true,
        color: DOCX_THEME.sectionText,
        size: 36,
      }),
    ],
  });
}

/** Subheading / card label tone (cyan accent). */
function subsectionTitle(text: string): Paragraph {
  return new Paragraph({
    spacing: { before: 160, after: 120 },
    children: [
      new TextRun({
        text,
        bold: true,
        color: DOCX_THEME.cardLabel,
        size: 24,
      }),
    ],
  });
}

function bodyParagraph(text: string, spacingAfter = 200): Paragraph {
  return new Paragraph({
    spacing: {
      after: spacingAfter,
      line: 276,
      lineRule: LineRuleType.AUTO,
    },
    children: [new TextRun({ text, color: DOCX_THEME.body, size: 24 })],
  });
}

function bulletParagraph(text: string, spacingAfter = 90): Paragraph {
  return new Paragraph({
    spacing: { after: spacingAfter },
    indent: { left: 360 },
    children: [
      new TextRun({ text: '• ', color: DOCX_THEME.body, size: 24 }),
      new TextRun({ text, color: DOCX_THEME.body, size: 24 }),
    ],
  });
}

function labeledLine(label: string, value: string, spacingAfter = 140): Paragraph {
  return new Paragraph({
    spacing: { after: spacingAfter },
    children: [
      new TextRun({ text: `${label}: `, bold: true, color: DOCX_THEME.cardLabel, size: 22 }),
      new TextRun({ text: value || '—', color: DOCX_THEME.body, size: 24 }),
    ],
  });
}

function spacerParagraph(after = 180): Paragraph {
  return new Paragraph({ spacing: { after } });
}

function tableHeaderParagraph(text: string): Paragraph {
  return new Paragraph({
    children: [new TextRun({ text, bold: true, color: DOCX_THEME.sectionText, size: 22 })],
  });
}

function tableBodyParagraph(text: string): Paragraph {
  return new Paragraph({
    children: [new TextRun({ text, color: DOCX_THEME.body, size: 22 })],
  });
}

export async function buildResearchProjectDocxBlob(data: Config): Promise<Blob> {
  const blocks: (Paragraph | Table)[] = [];

  const projectTitle = asStr(data.project_title) || asStr(data.title) || 'Research project';
  const shortTitle = asStr(data.short_title);
  const status = asStr(data.status);
  const studyType = asStr(data.study_type);
  const studySite = asStr(data.study_site);
  const areas = asStrArr(data.clinical_area);
  const mechanistic = isAcuteMechanisticProfile(data, studyType);

  blocks.push(buildCoverTable(projectTitle, shortTitle, status, studySite, areas, studyType));
  blocks.push(spacerParagraph(240));

  const synopsis = asStr(data.protocol_synopsis_text);
  if (synopsis) {
    blocks.push(sectionTitle('Protocol synopsis'));
    blocks.push(bodyParagraph(synopsis, 240));
  }

  const summary = asStr(data.project_summary);
  if (summary) {
    blocks.push(sectionTitle('Summary'));
    blocks.push(bodyParagraph(summary, 240));
  }

  const rationale = asStrArr(data.scientific_rationale);
  if (rationale.length) {
    blocks.push(sectionTitle('Scientific rationale'));
    rationale.forEach((line) => blocks.push(bulletParagraph(line, 80)));
    blocks.push(spacerParagraph(160));
  }

  const objectives =
    data.objectives && typeof data.objectives === 'object' && !Array.isArray(data.objectives)
      ? (data.objectives as Record<string, unknown>)
      : {};
  const hasObj =
    asStr(objectives.primary).trim() ||
    asStr(objectives.secondary).trim() ||
    asStr(objectives.exploratory).trim();
  if (hasObj) {
    blocks.push(sectionTitle('Objectives'));
    (['primary', 'secondary', 'exploratory'] as const).forEach((k) => {
      const t = asStr(objectives[k]).trim();
      if (t) blocks.push(labeledLine(k.charAt(0).toUpperCase() + k.slice(1), t));
    });
    blocks.push(spacerParagraph(160));
  }

  const hypothesis = asStr(data.hypothesis);
  if (hypothesis) {
    blocks.push(sectionTitle('Hypothesis'));
    blocks.push(bodyParagraph(hypothesis, 240));
  }

  const irb =
    data.irb && typeof data.irb === 'object' && !Array.isArray(data.irb)
      ? (data.irb as Record<string, unknown>)
      : {};
  const irbEntries = Object.entries(irb).filter(([, v]) => {
    if (v == null || v === '') return false;
    if (Array.isArray(v)) return v.length > 0;
    return true;
  });
  if (irbEntries.length) {
    blocks.push(sectionTitle('IRB & compliance'));
    irbEntries.forEach(([k, v]) => {
      const lab = humanizeKey(k);
      if (Array.isArray(v)) {
        blocks.push(subsectionTitle(lab));
        v.forEach((x) => blocks.push(bulletParagraph(String(x), 60)));
      } else if (typeof v === 'boolean') {
        blocks.push(labeledLine(lab, v ? 'Yes' : 'No'));
      } else {
        blocks.push(labeledLine(lab, String(v)));
      }
    });
    blocks.push(spacerParagraph(160));
  }

  const cohort =
    data.cohort && typeof data.cohort === 'object' && !Array.isArray(data.cohort)
      ? (data.cohort as Record<string, unknown>)
      : {};
  if (
    cohort.target_sample_size != null ||
    asStr(cohort.population) ||
    (Array.isArray(cohort.arms) && cohort.arms.length > 0) ||
    asStr(cohort.expected_dropout_rate) ||
    asStr(cohort.follow_up_duration)
  ) {
    blocks.push(sectionTitle('Cohort'));
    if (cohort.target_sample_size != null) {
      blocks.push(labeledLine('Target N', String(cohort.target_sample_size)));
    }
    if (asStr(cohort.population)) blocks.push(labeledLine('Population', asStr(cohort.population)));
    cohortArmsForReadView(cohort.arms).forEach((arm, i) => {
      const a = arm as Record<string, unknown>;
      blocks.push(
        labeledLine(
          asStr(a.name) || `Arm ${i + 1}`,
          `Planned size: ${a.size != null ? String(a.size) : '—'}`
        )
      );
    });
    if (asStr(cohort.expected_dropout_rate)) {
      blocks.push(labeledLine('Expected dropout', asStr(cohort.expected_dropout_rate)));
    }
    if (asStr(cohort.follow_up_duration)) {
      blocks.push(labeledLine('Follow-up', asStr(cohort.follow_up_duration)));
    }
    blocks.push(spacerParagraph(160));
  }

  const eligibility =
    data.eligibility && typeof data.eligibility === 'object' && !Array.isArray(data.eligibility)
      ? (data.eligibility as Record<string, unknown>)
      : {};
  const inc = asStrArr(eligibility.inclusion_criteria);
  const exc = asStrArr(eligibility.exclusion_criteria);
  if (inc.length || exc.length) {
    blocks.push(sectionTitle('Eligibility'));
    if (inc.length) {
      blocks.push(subsectionTitle('Inclusion'));
      inc.forEach((x) => blocks.push(bulletParagraph(x, 60)));
    }
    if (exc.length) {
      blocks.push(subsectionTitle('Exclusion'));
      exc.forEach((x) => blocks.push(bulletParagraph(x, 60)));
    }
    blocks.push(spacerParagraph(160));
  }

  const intervention =
    data.intervention && typeof data.intervention === 'object' && !Array.isArray(data.intervention)
      ? (data.intervention as Record<string, unknown>)
      : {};
  if (
    asStr(intervention.device_type) ||
    asStr(intervention.acute_session_summary) ||
    intervention.active_protocol != null
  ) {
    blocks.push(sectionTitle('Intervention'));
    if (asStr(intervention.device_type)) {
      blocks.push(labeledLine('Device', asStr(intervention.device_type)));
    }
    if (asStr(intervention.study_mode)) {
      blocks.push(labeledLine('Study mode', asStr(intervention.study_mode)));
    }
    if (asStr(intervention.acute_session_summary)) {
      blocks.push(subsectionTitle('Acute stimulation session'));
      blocks.push(bodyParagraph(asStr(intervention.acute_session_summary), 160));
    }
    if (intervention.active_protocol != null) {
      blocks.push(subsectionTitle('Active protocol'));
      activeProtocolLines(intervention.active_protocol).forEach((line) =>
        blocks.push(bodyParagraph(line, 70))
      );
    }
    blocks.push(spacerParagraph(160));
  }

  const visitsRaw = Array.isArray(data.study_visits) ? data.study_visits : [];
  const visitsFiltered = visitsRaw.filter((v) => {
    if (!v || typeof v !== 'object' || Array.isArray(v)) return true;
    return !shouldOmitRandomizationVisit(v as Record<string, unknown>, mechanistic, data);
  });
  if (visitsFiltered.length) {
    blocks.push(sectionTitle('Study visits'));
    visitsFiltered.forEach((v, i) => {
      const visit = v as Record<string, unknown>;
      const title = visitRecordLabel(visit) || `Visit ${i + 1}`;
      blocks.push(subsectionTitle(title));
      if (asStr(visit.window)) blocks.push(labeledLine('Timing / window', asStr(visit.window)));
      if (asStr(visit.notes)) blocks.push(bodyParagraph(asStr(visit.notes), 100));
      const procs = Array.isArray(visit.procedures) ? visit.procedures : [];
      procs.forEach((p) => blocks.push(bulletParagraph(String(p), 60)));
      blocks.push(spacerParagraph(120));
    });
  }

  const outcomes =
    data.outcomes && typeof data.outcomes === 'object' && !Array.isArray(data.outcomes)
      ? (data.outcomes as Record<string, unknown>)
      : {};
  const hasOutcomes =
    outcomeEndpointList(outcomes, 'primary').length > 0 ||
    outcomeEndpointList(outcomes, 'secondary').length > 0 ||
    outcomeEndpointList(outcomes, 'exploratory').length > 0 ||
    asStrArr(outcomes.autonomic_endpoints).length > 0;
  if (hasOutcomes) {
    blocks.push(sectionTitle('Outcomes'));
    (['primary', 'secondary', 'exploratory'] as const).forEach((k) => {
      const arr = outcomeEndpointList(outcomes, k);
      if (!arr.length) return;
      const label =
        k === 'primary' ? 'Primary endpoints' : k === 'secondary' ? 'Secondary endpoints' : 'Exploratory endpoints';
      blocks.push(subsectionTitle(label));
      arr.forEach((x) => blocks.push(bulletParagraph(x, 60)));
    });
    if (asStrArr(outcomes.autonomic_endpoints).length) {
      blocks.push(subsectionTitle('Autonomic endpoints'));
      asStrArr(outcomes.autonomic_endpoints).forEach((x) => blocks.push(bulletParagraph(x, 60)));
    }
    blocks.push(spacerParagraph(160));
  }

  const dataCollection =
    data.data_collection && typeof data.data_collection === 'object' && !Array.isArray(data.data_collection)
      ? (data.data_collection as Record<string, unknown>)
      : {};
  if (
    asStrArr(dataCollection.modalities).length ||
    asStr(dataCollection.data_storage) ||
    asStrArr(dataCollection.data_quality).length
  ) {
    blocks.push(sectionTitle('Data collection'));
    if (asStrArr(dataCollection.modalities).length) {
      blocks.push(subsectionTitle('Modalities'));
      asStrArr(dataCollection.modalities).forEach((x) => blocks.push(bulletParagraph(x, 60)));
    }
    if (asStr(dataCollection.data_storage)) {
      blocks.push(labeledLine('Storage', asStr(dataCollection.data_storage)));
    }
    if (asStrArr(dataCollection.data_quality).length) {
      blocks.push(subsectionTitle('Data quality'));
      asStrArr(dataCollection.data_quality).forEach((x) => blocks.push(bulletParagraph(x, 60)));
    }
    blocks.push(spacerParagraph(160));
  }

  const stats =
    data.statistical_analysis && typeof data.statistical_analysis === 'object' && !Array.isArray(data.statistical_analysis)
      ? (data.statistical_analysis as Record<string, unknown>)
      : {};
  if (Object.keys(stats).length || mechanistic) {
    blocks.push(sectionTitle('Statistical analysis'));
    const narrative = asStr(stats.summary_narrative) || asStr(stats.narrative) || asStr(stats.synopsis);
    if (narrative) blocks.push(bodyParagraph(narrative, 180));
    else if (mechanistic) {
      blocks.push(
        bodyParagraph(
          'Planned analyses emphasize within-subject stimulus–response relationships, descriptive summaries, and exploratory correlations suited to a mechanistic pilot.',
          180
        )
      );
    }
    Object.entries(stats)
      .filter(([k]) => !['summary_narrative', 'narrative', 'synopsis'].includes(k))
      .filter(([, v]) => v != null && v !== '')
      .forEach(([k, v]) => {
        blocks.push(
          labeledLine(
            humanizeKey(k),
            typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v)
          )
        );
      });
    blocks.push(spacerParagraph(160));
  }

  const budget =
    data.budget && typeof data.budget === 'object' && !Array.isArray(data.budget)
      ? (data.budget as Record<string, unknown>)
      : {};
  const categories = Array.isArray(budget.categories) ? budget.categories : [];
  const optional = optionalBudgetList(budget);
  if (categories.length || optional.length || asStr(budget.currency)) {
    blocks.push(sectionTitle('Budget'));
    const currency = asStr(budget.currency) || 'USD';
    blocks.push(labeledLine('Currency', currency));
    if (categories.length) {
      const header = new TableRow({
        children: ['Category', 'Type', 'Total', 'Notes'].map(
          (h) =>
            new TableCell({
              shading: { fill: DOCX_THEME.tableHeaderBg },
              margins: { top: 80, bottom: 80, left: 100, right: 100 },
              children: [tableHeaderParagraph(h)],
            })
        ),
      });
      const rows: TableRow[] = [header];
      for (const row of categories) {
        const r = row as Record<string, unknown>;
        rows.push(
          new TableRow({
            children: [
              new TableCell({
                margins: { top: 80, bottom: 80, left: 100, right: 100 },
                children: [tableBodyParagraph(asStr(r.name))],
              }),
              new TableCell({
                margins: { top: 80, bottom: 80, left: 100, right: 100 },
                children: [tableBodyParagraph(asStr(r.cost_type))],
              }),
              new TableCell({
                margins: { top: 80, bottom: 80, left: 100, right: 100 },
                children: [tableBodyParagraph(r.total != null ? String(r.total) : '—')],
              }),
              new TableCell({
                margins: { top: 80, bottom: 80, left: 100, right: 100 },
                children: [tableBodyParagraph(asStr(r.notes))],
              }),
            ],
          })
        );
      }
      blocks.push(
        new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          borders: {
            top: TABLE_BORDER,
            bottom: TABLE_BORDER,
            left: TABLE_BORDER,
            right: TABLE_BORDER,
            insideHorizontal: TABLE_BORDER,
            insideVertical: TABLE_BORDER,
          },
          rows,
        })
      );
      blocks.push(spacerParagraph(180));
    }
    if (optional.length) {
      blocks.push(subsectionTitle('Optional costs'));
      optional.forEach((row) => {
        const r = row as Record<string, unknown>;
        const total = r.estimated_total ?? r.suggested_total;
        blocks.push(
          labeledLine(
            asStr(r.name),
            `${total != null ? String(total) : '—'}${asStr(r.notes) ? ` — ${asStr(r.notes)}` : ''}`
          )
        );
      });
    }
    if (budget.estimated_total != null) {
      blocks.push(labeledLine('Estimated total', String(budget.estimated_total)));
    }
    blocks.push(spacerParagraph(160));
  }

  const refs = parseScientificReferences(data.scientific_references);
  if (refs.length) {
    blocks.push(sectionTitle('Scientific references'));
    refs.forEach((r, i) => {
      blocks.push(
        new Paragraph({
          spacing: { after: 120 },
          indent: { left: 240 },
          children: [
            new TextRun({ text: `${i + 1}. `, bold: true, color: DOCX_THEME.body, size: 24 }),
            new TextRun({ text: formatReferenceLine(r), color: DOCX_THEME.body, size: 24 }),
          ],
        })
      );
    });
  }

  const doc = new Document({
    title: projectTitle.slice(0, 200),
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: convertInchesToTwip(0.75),
              bottom: convertInchesToTwip(0.75),
              left: convertInchesToTwip(0.9),
              right: convertInchesToTwip(0.9),
            },
          },
        },
        children: blocks,
      },
    ],
  });

  return Packer.toBlob(doc);
}

export function triggerDocxDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.docx') ? filename : `${filename}.docx`;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
